import * as Yup from "yup";
import { DRIVER_OPERATIONAL_STATUS_OPTIONS } from "../../../constants/driverStatus";

const emailRegExp = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
const mobileRegExp = /^[6-9]\d{9}$/; // Starts with 6-9 and total 10 digits
const pincodeRegExp = /^[1-9][0-9]{5}$/; // 6 digits, doesn't start with 0
const panRegExp = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
const aadhaarRegExp = /^[2-9]{1}[0-9]{3}[0-9]{4}[0-9]{4}$/; // Aadhaar doesn't start with 0 or 1
const ifscRegExp = /^[A-Z]{4}0[A-Z0-9]{6}$/; // Example: SBIN0123456
const nameRegExp = /^[a-zA-Z ]+$/;
const addressRegExp = /^[A-Za-z0-9\s,./#-]+$/;

// Verhoeff algorithm for Aadhaar validation
const verhoeff = {
  d: [
    [0, 1, 2, 3, 4, 5, 6, 7, 8, 9],
    [1, 2, 3, 4, 0, 6, 7, 8, 9, 5],
    [2, 3, 4, 0, 1, 7, 8, 9, 5, 6],
    [3, 4, 0, 1, 2, 8, 9, 5, 6, 7],
    [4, 0, 1, 2, 3, 9, 5, 6, 7, 8],
    [5, 9, 8, 7, 6, 0, 4, 3, 2, 1],
    [6, 5, 9, 8, 7, 1, 0, 4, 3, 2],
    [7, 6, 5, 9, 8, 2, 1, 0, 4, 3],
    [8, 7, 6, 5, 9, 3, 2, 1, 0, 4],
    [9, 8, 7, 6, 5, 4, 3, 2, 1, 0],
  ],
  p: [
    [0, 1, 2, 3, 4, 5, 6, 7, 8, 9],
    [1, 5, 7, 6, 2, 8, 3, 0, 9, 4],
    [5, 8, 0, 3, 7, 9, 6, 1, 4, 2],
    [8, 9, 1, 6, 0, 4, 3, 5, 2, 7],
    [9, 4, 5, 3, 1, 2, 6, 8, 7, 0],
    [4, 2, 8, 6, 5, 7, 3, 9, 0, 1],
    [2, 7, 9, 3, 8, 0, 6, 4, 1, 5],
    [7, 0, 4, 6, 9, 1, 3, 2, 5, 8],
  ],
  inv: [0, 4, 3, 2, 1, 5, 6, 7, 8, 9],

  validate(number) {
    if (!/^\d{12}$/.test(number)) return false;
    let c = 0;
    let invertedArray = number.split("").reverse().map(Number);
    for (let i = 0; i < invertedArray.length; i++) {
      c = this.d[c][this.p[i % 8][invertedArray[i]]];
    }
    return c === 0;
  },
};

const today = new Date();
today.setHours(0, 0, 0, 0);

const driverValidationSchema = Yup.object().shape({

  uniqueDriverId: Yup.string()
    .trim()
    .matches(/^DRV-[A-Z0-9]{5}$/, "Invalid Driver Unique ID")
    .required("Driver Unique ID is required"),

  // Driver Information
  name: Yup.string()
    .trim()
    .test('not-empty', 'Driver name cannot be empty', val => !!val && val.trim().length > 0)
    .required("Driver name is required"),

  address: Yup.string()
    .trim()
    .test('not-empty', 'Address cannot be empty', val => !!val && val.trim().length > 0)
    .min(10, "Please enter a complete address (min 10 characters)")
    .max(50, "Address must not exceed 50 characters")
    .matches(addressRegExp, "Address can only contain letters, numbers, spaces, commas, periods, slashes, # and hyphens")
    .test(
      "address-has-alphanumeric",
      "Address must contain at least one letter or number",
      (value) => !value || /[A-Za-z0-9]/.test(value),
    )
    .required("Address is required"),

  city: Yup.string()
    .trim()
    .test('not-empty', 'City cannot be empty', val => !!val && val.trim().length > 0)
    .matches(nameRegExp, "City name should only contain alphabets")
    .max(20, "City name should be less than 20 characters long")
    .required("City is required"),

  state: Yup.string()
    .trim()
    .test('not-empty', 'State cannot be empty', val => !!val && val.trim().length > 0)
    .required("State is required"),

  pincode: Yup.string()
    .matches(pincodeRegExp, "Invalid pincode (6 digits)")
    .required("Pincode is required"),

  dob: Yup.date().required("Date of birth is required"),

  // Contact Details
  email: Yup.string()
    .transform((value) => value?.toLowerCase().trim())
    .matches(emailRegExp, "Invalid email format")
    .required("Email is required"),

  mobileNumber: Yup.string()
    .matches(mobileRegExp, "Enter a valid 10-digit Indian mobile number")
    .required("Mobile number is required"),

  whatsappNumber: Yup.string()
    .matches(mobileRegExp, "Enter a valid 10-digit WhatsApp number")
    .required("WhatsApp number is required"),

  // Emergency Contact
  emergencyContactName: Yup.string()
    .trim()
    .test('not-empty', 'Emergency contact name cannot be empty', val => !!val && val.trim().length > 0)
    .matches(nameRegExp, "Emergency contact name should only contain alphabets")
    .required("Emergency contact name is required"),

  emergencyContactNumber: Yup.string()
    .matches(mobileRegExp, "Must be a valid 10-digit number")
    .required("Emergency contact number is required"),

  emergencyContactRelationship: Yup.string()
    .trim()
    .max(20, "Relationship should be less than 20 characters long")
    .required("Relationship is required"),

  // Bank Details (optional)
  accountHolderName: Yup.string()
    .trim()
    .nullable()
    .notRequired()
    .test(
      "account-holder-name",
      "Account holder name should only contain alphabets",
      (value) => !value || nameRegExp.test(value)
    )
    .test(
      "account-holder-name-length",
      "Account holder name should be between 3 and 25 characters",
      (value) => !value || (value.length >= 3 && value.length <= 25)
    ),

  accountNumber: Yup.string()
    .nullable()
    .notRequired()
    .test(
      "account-number",
      "Account number should be 9-18 digits",
      (value) => !value || /^\d{9,18}$/.test(value)
    ),

  ifscCode: Yup.string()
    .nullable()
    .notRequired()
    .test(
      "ifsc-code",
      "Invalid IFSC code format",
      (value) => !value || ifscRegExp.test(value)
    ),

  bankName: Yup.string()
    .trim()
    .nullable()
    .notRequired()
    .test(
      "bank-name",
      "Bank name should only contain alphabets",
      (value) => !value || nameRegExp.test(value)
    )
    .test(
      "bank-name-length",
      "Bank name should be less than 40 characters",
      (value) => !value || value.length <= 40
    ),

  // License
  drivingLicenseNumber: Yup.string()
    .max(18)
    .required("Driving license number is required"),

  drivingLicenseExpiryDate: Yup.date()
    .min(today, "License expiry date cannot be in the past")
    .required("License expiry date is required"),

  drivingLicenseType: Yup.string().required("License type is required"),

  // Documents
  aadhaarCardNumber: Yup.string()
    .matches(/^\d{12}$/, "Aadhaar must be exactly 12 digits")
    .required("Aadhaar number is required"),

  panCardNumber: Yup.string()
    .matches(panRegExp, "Invalid PAN card number format")
    .required("PAN number is required"),

  // Additional
  driverExperience: Yup.number()
    .transform((_, val) => (val === "" ? null : Number(val)))
    .min(0)
    .max(50)
    .required("Driver experience is required"),

  education: Yup.string()
    .trim()
    .test('not-empty', 'Education cannot be empty', val => !!val && val.trim().length > 0)
    .min(2)
    .max(100, "Education should be less than 100 characters")
    .required("Education is required"),

  languagesKnown: Yup.string()
    .trim()
    .required("Languages Known are required"),

  operationalStatus: Yup.string()
    .oneOf(DRIVER_OPERATIONAL_STATUS_OPTIONS, "Invalid operational status")
    .required("Operational status is required"),

  accountStatus: Yup.string()
    .oneOf(["active", "inactive", "suspended", "blacklisted"], "Invalid account status")
    .required("Account status is required"),

  // Images
  drivingLicenseFront: Yup.mixed().required("Driving License Front is required"),
  drivingLicenseBack: Yup.mixed().required("Driving License Back is required"),
  aadhaarCardFront: Yup.mixed().required("Aadhaar Card Front is required"),
  aadhaarCardBack: Yup.mixed().required("Aadhaar Card Back is required"),
  panCardFront: Yup.mixed().required("PAN Card Front is required"),
});

export default driverValidationSchema;
